import { useState, useEffect } from "react";
import {
    Box, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Input, Button, Flex, Text, Spinner, HStack
} from "@chakra-ui/react";
import { getKategorijeVrijednosti, updateKategorijeVrijednosti } from "../../util/apiKnjiga";

const KategorijaVrijednost = ({ knjigaId, namjenaKey, onClose, onSave }) => {

    const [rows, setRows]       = useState([]);
    const [edited, setEdited]   = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState("");

    // ── Dohvat ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!knjigaId || !namjenaKey) return;
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await getKategorijeVrijednosti(namjenaKey, knjigaId);
                if (res.status === 200) {
                    setRows(res.res);
                } else {
                    throw new Error(res.message);
                }
            } catch (ex) {
                setError(ex.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [knjigaId, namjenaKey]);

    // ── Promjena vrijednosti ──────────────────────────────────────────────────
    const handleChange = (kategorija_id, value) => {
        if (value.length > 7) return;
        setEdited(prev => ({ ...prev, [kategorija_id]: value }));
    };

    // ── Trenutna vrijednost za input ──────────────────────────────────────────
    const currentVal = (row) =>
        edited[row.kategorija_id] !== undefined ? edited[row.kategorija_id] : row.val;

    const hasEdits = Object.keys(edited).length > 0;

    // ── Spremi ────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        // Validacija
        const invalid = Object.entries(edited).filter(
            ([, v]) => v === "" || isNaN(parseFloat(v)) || parseFloat(v) < 0 || v.length>7
        );
        if (invalid.length > 0) {
            setError("Sve vrijednosti moraju biti pozitivni brojevi.");
            return;
        }

        

        // Payload — samo izmijenjeni retci
        const payload = rows
            .filter(row => edited[row.kategorija_id] !== undefined)
            .map(row => ({
                kategorija_id: row.kategorija_id,
                val: parseFloat(edited[row.kategorija_id]),
            }));

        setSaving(true);
        setError("");
        try {
            const res = await updateKategorijeVrijednosti(knjigaId, payload);
            if (res.status === 200) {
                if (onSave) onSave();
            } else {
                throw new Error(res.message);
            }
        } catch (ex) {
            setError(ex.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Th stil ───────────────────────────────────────────────────────────────
    const thStyle = {
        fontSize: 'xs',
        textAlign: 'center',
        color: 'grunt2.800',
        bg: 'white',
        borderTopWidth: '1px',
        borderTopColor: 'black',
        borderBottomWidth: '1px',
        borderBottomColor: 'black',
        textTransform: 'none',
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Flex justify="center" align="center" h="8rem">
                <Spinner size="sm" color="grunt2.500" />
            </Flex>
        );
    }

    return (
        <Flex direction="column" h="100%" justify="space-between">

            {/* Greška */}
            {error && (
                <Text fontSize="xs" color="red.500" mb="0.5rem">{error}</Text>
            )}

            {/* Tablica */}
            <Box flex={1} overflowY="auto">
                <TableContainer>
                    <Table
                        variant="unstyled"
                        size="xs"
                        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
                    >
                        <Thead>
                            <Tr>
                                <Th {...thStyle} textAlign="center">Kategorija</Th>
                                <Th {...thStyle} textAlign="right">Starost od (dana)</Th>
                                <Th {...thStyle} textAlign="right">Starost do (dana)</Th>
                                <Th {...thStyle} textAlign="right">Površina (m²)</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((row, i) => {
                                const isEdited = edited[row.kategorija_id] !== undefined;
                                return (
                                    <Tr
                                        key={row.kategorija_id}
                                        bg={i % 2 === 0 ? 'white' : 'gray.50'}
                                        h="2rem"
                                    >
                                        <Td fontSize="xs" textAlign="center">{row.naziv}</Td>

                                        <Td fontSize="xs" textAlign="right" color="gray.500">
                                            {row.starost_dani_od}
                                        </Td>

                                        <Td fontSize="xs" textAlign="right" color="gray.500">
                                            {row.starost_dani_do}
                                        </Td>

                                        <Td textAlign="right" pr="0.5rem">
                                            <Input
                                                size="xs"
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                w="6rem"
                                                textAlign="right"
                                                focusBorderColor="grunt2.400"
                                                borderColor={isEdited ? 'red.300' : 'gray.200'}
                                                value={currentVal(row)}
                                                onChange={(e) => handleChange(row.kategorija_id, e.target.value)}
                                            />
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Gumbi */}
            <HStack justify="flex-end" pt="1rem" pb="0.5rem" spacing="0.75rem"
                    borderTopWidth="1px" borderTopColor="gray.200" mt="1rem">
                <Button
                    size="xs"
                    variant="outline"
                    colorScheme="gray"
                    onClick={onClose}
                >
                    Odustani
                </Button>
                <Button
                    size="xs"
                    colorScheme="grunt2"
                    isDisabled={!hasEdits}
                    isLoading={saving}
                    onClick={handleSave}
                >
                    Spremi
                </Button>
            </HStack>

        </Flex>
    );
};

export default KategorijaVrijednost;